import { Controller } from "@hotwired/stimulus";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

export default class extends Controller {
  static targets = ["mapContainer"];
  static values = { apiKey: String, locations: Array };

  async connect() {
    setOptions({ key: this.apiKeyValue, version: "weekly" });
    await this.initializeMap();

    const recenterBtn = document.getElementById("recenter-btn");
    if (recenterBtn) {
      recenterBtn.addEventListener("click", this.handleRecenter.bind(this));
    }
  }

  async initializeMap() {
    try {
      const { Map } = await importLibrary("maps");
      const { AdvancedMarkerElement } = await importLibrary("marker");

      const mapOptions = {
        zoom: 15,
        mapId: "locations",
        center: { lat: 34.08, lng: 74.8 }, // Default fallback
      };

      this.map = new Map(this.mapContainerTarget, mapOptions);

      this.centerOnBackendLocations();

      this.locationsValue.forEach((location) => {
        const markerTag = document.createElement("div");
        markerTag.className = "relative";

        markerTag.innerHTML = `
        <div class="w-12 h-12 bg-red-500 rounded-full border-4 border-white shadow-lg flex items-center justify-center relative z-10">
          <span class="text-white text-xs font-bold">${location.title.slice(0, 4)}</span>
        </div>
        <div class="absolute -bottom-3 left-4 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[12px] border-t-red-500 z-0">
        </div>`;

        const marker = new AdvancedMarkerElement({
          map: this.map,
          position: {
            lat: parseFloat(location.lat),
            lng: parseFloat(location.lng),
          },
          content: markerTag,
          title: location.title,
          gmpClickable: true,
        });

        marker.addListener("click", () => {
          this.showPreview(location);
        });
      });
    } catch (error) {
      console.error("Error initializing map:", error);
    }
  }

  showPreview(location) {
    const previewEl = document.getElementById("preview");
    previewEl.classList.remove("hidden");

    // 1. Calculate Discount Percentage dynamically
    let discountBadge = "";
    const originalPrice = parseFloat(location.original_price);
    const currentPrice = parseFloat(location.price);

    if (originalPrice && originalPrice > currentPrice) {
      const discountPercent = Math.round(
        ((originalPrice - currentPrice) / originalPrice) * 100,
      );
      discountBadge = `
        <div class="absolute top-2 left-2 bg-red-600 text-white px-2 py-0.5 rounded-full text-[10px] font-bold shadow-md border-2 border-white tracking-wide">
          ${discountPercent}% OFF
        </div>
      `;
    }

    // 2. Updated UI (bottom-10, smaller image, sleeker pill buttons, dynamic discount)
    previewEl.innerHTML = `
      <div class="absolute bottom-10 left-0 right-0 z-30 p-4" data-controller="modal geolocation"
        data-geolocation-latitude-value="${location.lat}" 
        data-geolocation-longitude-value="${location.lng}">
        
        <div class="bg-white rounded-[20px] shadow-[0_8px_30px_rgba(0,0,0,0.12)] overflow-hidden border border-gray-100">
          <div class="relative">
            <img src="${location.pic}" class="w-full h-32 object-cover" alt="${location.title}">
            <button class="absolute top-2 right-2 p-1.5 bg-black/50 rounded-full backdrop-blur-md hover:bg-black/70 transition-all active:scale-90" data-action="click->modal#close">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" class="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
            ${discountBadge}
          </div>

          <div class="p-3.5">
            <h3 class="text-gray-900 font-bold mb-0.5 truncate text-[15px] leading-tight">${location.title}</h3>
            <p class="text-[10px] text-gray-500 font-bold uppercase tracking-wide mb-2.5">${location.user}</p>

            <div class="flex items-center gap-2 mb-2 text-sm">
              <div class="flex items-center gap-1.5 text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-100/50 w-max">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                <span data-geolocation-target="location" class="font-bold text-[10px]">calculating...</span>
              </div>
            </div>

            <div class="flex items-baseline gap-1.5 mb-3.5">
              <span class="text-xl font-black text-gray-900 leading-none">₹${location.price}</span>
              ${originalPrice && originalPrice > currentPrice ? `<span class="text-[11px] font-medium text-gray-400 line-through">₹${location.original_price}</span>` : ""}
            </div>

            <div class="grid grid-cols-2 gap-2.5">
              <div data-geolocation-target="maplink" class="w-full">
                <button class="w-full flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 text-gray-800 font-semibold rounded-full hover:bg-gray-200 transition-all shadow-sm active:scale-95">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polygon points="3 11 22 2 13 21 11 13 3 11" />
                  </svg>
                  <span class="text-xs">Wait...</span>
                </button>
              </div>
              
              <a href="${location.link}" class="w-full flex items-center justify-center gap-1 px-3 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-full hover:opacity-90 transition-all shadow-sm active:scale-95" target="_top">
                <span class="text-xs">View Deal</span>
              </a>
            </div>
          </div>
        </div>
      </div>`;

    this.map.panTo({
      lat: parseFloat(location.lat),
      lng: parseFloat(location.lng),
    });
  }

  centerOnBackendLocations() {
    if (this.locationsValue.length > 0) {
      this.map.setCenter({
        lat: parseFloat(this.locationsValue[0].lat),
        lng: parseFloat(this.locationsValue[0].lng),
      });
    }
  }

  async handleRecenter(event) {
    event.preventDefault();

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userPos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          this.map.panTo(userPos);
          this.map.setZoom(15);
        },
        (error) => {
          console.error("Geolocation error:", error);
        },
      );
    }
  }

  disconnect() {
    const recenterBtn = document.getElementById("recenter-btn");
    if (recenterBtn) {
      recenterBtn.removeEventListener("click", this.handleRecenter);
    }
  }
}
